import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import { strings } from '@/shared/strings';
import { EyeIcon, EyeOffIcon } from '@/ui/icons';
import styles from './PasswordInput.module.css';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Every password field in the system: the field with its show and hide button
 * inside it, on the right. Request of 5 October 2026, section 4, which asks for
 * the toggle the sign in page had (request of 4 October 2026, section 1) on
 * every one of them, from one component.
 *
 * The button is a full 44 pixel target for a thumb and says which way it goes
 * for a screen reader. It is a plain button, never a submit, so pressing it
 * in the middle of a form sends nothing. Each field keeps its own state, so
 * showing the new password does not show the current one beside it.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { id, className, ...rest },
  ref,
) {
  const [shown, setShown] = useState(false);
  const fallbackId = useId();
  const inputId = id ?? fallbackId;

  return (
    <div className={styles.field}>
      <input
        ref={ref}
        id={inputId}
        type={shown ? 'text' : 'password'}
        className={className === undefined ? styles.input : `${className} ${styles.input}`}
        {...rest}
      />
      <button
        type="button"
        className={styles.reveal}
        aria-label={shown ? strings.login.hidePassword : strings.login.showPassword}
        aria-pressed={shown}
        aria-controls={inputId}
        onClick={() => setShown((value) => !value)}
      >
        {shown ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
      </button>
    </div>
  );
});
